import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FaPaperclip, FaDownload } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import HeaderCard from '../../components/Header/HeaderCard';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { formatMoney } from '../../components/Utils/FormatUtils';
import SyntheseAvances from '../Espace/SyntheseAvances';
import TableauRemboursements from '../Espace/TableauRemboursements';
import { ouvrirJustificatif } from '../Espace/justificatif';
import PiecesJointes from '../Espace/PiecesJointes';
import { useATraiter } from '../../components/Navigation/ATraiter';

// Même refonte que l'espace salarié, à la mesure du rôle : trois chiffres en
// haut, la file de décision, puis les remboursements en cours de la société.
const LIBELLES_SYNTHESE_RH = {
  attente: 'supervisionRh.synthese.attente',
  reste: 'supervisionRh.synthese.reste',
  prochain: 'supervisionRh.synthese.prochain',
};

/**
 * Supervision RH des demandes (bolt 046, Yros/Kavero#71), devenue écran de
 * décision (T4 de YRH-SHARED-005, Yros/Kavero#137).
 *
 * Patron copié de `PresenceDuJour.js` (bolt 032) : écran RH consommant une
 * route proxy à zéro paramètre de requête, `TableList` SANS
 * `pagination`/`onPageChange` — la route ne les accepte pas — filtre et
 * recherche LOCAUX via `useMemo`, jamais un second appel réseau.
 *
 * Le repli type/période reprend `MesDemandes.js` (bolt 040) SANS son repli
 * `type_conge`/`date_debut` plats : aucune donnée Sprint 1 ne subsiste
 * (intent, « coupure nette »).
 *
 * **Les actions vivent ici plutôt que sur l'écran de validation des congés.**
 * Celui-ci est déjà générique en lecture, déjà gardé au bon seuil de rôle, il
 * affiche déjà le motif de refus et montre toutes les demandes de la société.
 * L'autre a ses colonnes et son action propres au congé.
 *
 * Trois règles que l'écran s'impose : le **type voyage** avec la décision,
 * parce que le proxy y choisit le couple modèle/méthode dans une table fermée
 * (Yros/Kavero#261) ; un **refus sans motif ne part pas**, le motif étant la
 * seule chose qui dise au salarié pourquoi ; et l'**état affiché vient du
 * serveur** — la liste se recharge après chaque décision, un état local
 * optimiste mentirait dès qu'Odoo refuse.
 */

// Badge d'état : couleurs des clés RÉELLES de l'enveloppe C10
// (`validee`/`refusee`), jamais les clés Odoo brutes `validate`/`refuse` —
// c'est le bug déjà repéré dans `MesDemandes.js`/`ValidationDemandes.js`
// (plan § Existant), pas reproduit ici.
// `remise` en indigo, jamais le bleu générique (feedback du 2026-09-25) —
// même teinte que l'espace salarié (`MesAttestations.js`), pour qu'un état
// final se lise pareil des deux côtés de l'écran.
const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
  remise: 'bg-indigo-100 text-indigo-800',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

const FILTRES = ['tous', 'en_attente', 'validee', 'refusee'];

// Contrat à 4 formes (plan § Symboles) : `null`/absent → '—' ; soldes déjà
// vide malgré `ok:true` → '—' aussi, pas de liste vide silencieuse ;
// `{ok:false, message}` → message tel quel, jamais reformulé ;
// `{ok:true, soldes:[...]}` → libellés et restants joints.
function afficherSolde(solde) {
  if (!solde) {
    return '—';
  }
  if (solde.ok === false) {
    return solde.message;
  }
  if (solde.ok === true) {
    const soldes = Array.isArray(solde.soldes) ? solde.soldes : [];
    if (soldes.length === 0) {
      return '—';
    }
    return soldes.map((s) => `${s.libelle}: ${s.restant}`).join(', ');
  }
  return '—';
}

/**
 * Mois de retenue en clair — « octobre 2026 » plutôt que « 2026-10 ».
 *
 * Le premier du mois est construit en heure LOCALE (`...-01T00:00:00`, sans
 * `Z`) : avec un instant UTC, un poste à l'ouest de Greenwich afficherait le
 * mois précédent.
 */
function formaterMois(mois) {
  if (typeof mois !== 'string' || !/^\d{4}-\d{2}$/.test(mois)) {
    return '';
  }
  const date = new Date(`${mois}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return mois;
  }
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(date);
}

/**
 * Date de dépôt en clair, découpée plutôt que parsée.
 *
 * Odoo rend « AAAA-MM-JJ hh:mm:ss » en UTC. Le passer à `new Date` puis à un
 * formateur local décalerait le jour d'un cran pour une demande déposée en
 * soirée — la file afficherait la veille. On garde donc la date telle qu'Odoo
 * l'a écrite, sans inventer de conversion.
 */
function formaterDate(valeur) {
  if (typeof valeur !== 'string') {
    return '—';
  }
  const jour = valeur.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) {
    return '—';
  }
  const [annee, mois, quantieme] = jour.split('-');
  return `${quantieme}/${mois}/${annee}`;
}

// Les trois demandes d'argent. Elles partagent ce qu'une file de décision doit
// montrer — un montant, et la période sur laquelle il se retient — là où un
// congé parle en dates.
const TYPES_MONETAIRES = ['acompte', 'avance', 'pret'];

/**
 * La période sur laquelle la retenue portera, ou rien.
 *
 * Un acompte porte sur le mois en cours : il n'a pas de période à montrer, et
 * lui en inventer une laisserait croire à un choix du salarié.
 */
function periodeMonetaire(demande) {
  const contenu = demande.contenu || {};
  if (demande.type === 'avance') {
    return formaterMois(contenu.mois_retenue) || null;
  }
  if (demande.type === 'pret' && contenu.debut && contenu.fin) {
    return `${formaterMois(contenu.debut)} → ${formaterMois(contenu.fin)}`;
  }
  return null;
}

/**
 * Ce que le circuit de validation dit de la demande (Yros/Kavero#468) : elle
 * attend le manager, il l'a approuvée, la RH a décidé à sa place, ou l'étape
 * a été sautée. Rien pour une demande « RH seule » ou d'avant la chaîne.
 */
function EtapeManager({ demande }) {
  const { t } = useTranslation();
  const circuit = demande.circuit;
  if (!circuit || circuit.circuit === 'rh') return null;
  const nom = circuit.manager?.nom || '';
  let texte = null;
  if (circuit.a_la_place) texte = t('supervisionRh.aLaPlace');
  else if (circuit.etape === 'manager') texte = t('supervisionRh.attenteManager', { nom });
  else if (circuit.decision_manager === 'approuvee') texte = t('supervisionRh.approuveeManager', { nom });
  else if (circuit.note) texte = circuit.note;
  if (!texte) return null;
  return (
    <span data-testid={`supervision-etape-${demande.id}`} className="text-xs text-gray-500">
      {texte}
    </span>
  );
}

function SupervisionRh() {
  const { t, i18n } = useTranslation();
  const localeSynthese = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';

  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  // Les actions que la RH peut retenir sur une demande « Autres »
  // (Yros/Kavero#433), et celle choisie pour chaque demande, par identifiant.
  const [actionsAutres, setActionsAutres] = useState([]);
  const [actionChoisie, setActionChoisie] = useState({});
  const actionsLues = useRef(false);
  // Le filtre d'état vit dans l'adresse (Yros/Kavero#416) : la boîte
  // « À traiter » ouvre la liste déjà filtrée sur `?etat=en_attente`.
  const [parametres, setParametres] = useSearchParams();
  const filtreEtat = FILTRES.includes(parametres.get('etat')) ? parametres.get('etat') : 'tous';
  const setFiltreEtat = (cle) =>
    setParametres((courants) => {
      const suivants = new URLSearchParams(courants);
      if (cle === 'tous') suivants.delete('etat');
      else suivants.set('etat', cle);
      return suivants;
    }, { replace: true });
  const { relire: relireATraiter } = useATraiter();
  const [recherche, setRecherche] = useState('');

  // Décision : la demande en cours d'envoi, celle dont on saisit le motif, et
  // le refus métier d'Odoo. Trois états distincts — les confondre ferait
  // disparaître le tableau à la première erreur de décision.
  const [decisionEnCours, setDecisionEnCours] = useState(null);
  const [refusPour, setRefusPour] = useState(null);
  const [motif, setMotif] = useState('');
  const [erreurDecision, setErreurDecision] = useState(null);

  // Remise en main propre (Yros/Kavero#122) : état distinct de
  // `decisionEnCours`, qui porte l'approbation/le refus — la remise
  // n'apparaît qu'une fois la demande validée.
  const [remiseEnCours, setRemiseEnCours] = useState(null);

  // Numéro de la dernière requête lancée. Deux chargements peuvent se croiser —
  // le double montage de `StrictMode` en développement, un rechargement parti
  // pendant qu'un autre est en vol — et leurs réponses arrivent dans n'importe
  // quel ordre. C'est la dernière requête lancée qui fait foi : la réponse
  // d'une requête dépassée est ignorée, succès comme échec.
  const derniereRequete = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniereRequete.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.listerSupervisionRh();
      if (numero !== derniereRequete.current) return;
      // Les déclarations d'heures se décident dans « Feuilles de temps »
      // (Yros/Kavero#220) : la route les rend encore, la file ne les montre plus.
      setDemandes((reponse?.demandes || []).filter((demande) => demande.type !== 'heures_sup'));
      setErreur(null);
    } catch (e) {
      if (numero !== derniereRequete.current) return;
      signalerErreur(e, 'SupervisionRh.charger');
      setDemandes([]);
      setErreur(t('supervisionRh.erreurGenerique'));
    } finally {
      if (numero === derniereRequete.current) setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  // La liste des actions ne se lit qu'une fois, et seulement s'il y a une
  // demande « Autres » à décider : les autres types n'en ont aucun usage. Une
  // lecture en échec laisse la liste vide — la décision reste possible, sans
  // suite particulière.
  useEffect(() => {
    if (actionsLues.current) return;
    if (!demandes.some((d) => d.type === 'autres' && d.etat === 'en_attente')) return;
    actionsLues.current = true;
    (async () => {
      try {
        const reponse = await apiService.listerActionsAutres();
        setActionsAutres(reponse?.ok ? reponse.actions || [] : []);
      } catch (e) {
        signalerErreur(e, 'SupervisionRh.chargerActionsAutres');
        setActionsAutres([]);
      }
    })();
  }, [demandes]);

  // Un refus métier remonte en 409, donc par la branche d'erreur d'axios :
  // c'est là que vit le message d'Odoo, et il s'affiche tel quel.
  const messageDeRefus = (e) =>
    e?.response?.data?.message || t('supervisionRh.erreurGenerique');

  const approuver = async (demande) => {
    if (decisionEnCours) return;
    setDecisionEnCours(demande.id);
    setErreurDecision(null);
    try {
      const actionId = demande.type === 'autres' ? Number(actionChoisie[demande.id]) : 0;
      if (actionId > 0) {
        await apiService.approuverDemandeRh(demande.id, demande.type, actionId);
      } else {
        await apiService.approuverDemandeRh(demande.id, demande.type);
      }
      relireATraiter();
      await charger();
    } catch (e) {
      setErreurDecision(messageDeRefus(e));
    } finally {
      setDecisionEnCours(null);
    }
  };

  const confirmerLeRefus = async () => {
    // Un motif fait d'espaces n'est pas un motif : rien ne part.
    if (!refusPour || !motif.trim() || decisionEnCours) return;
    setDecisionEnCours(refusPour.id);
    setErreurDecision(null);
    try {
      await apiService.refuserDemandeRh(refusPour.id, refusPour.type, motif.trim());
      relireATraiter();
      setRefusPour(null);
      setMotif('');
      await charger();
    } catch (e) {
      setErreurDecision(messageDeRefus(e));
    } finally {
      setDecisionEnCours(null);
    }
  };

  // La RH confirme avoir donné le document en main propre au salarié (Yros/Kavero#122,
  // troisième revirement) : sans ce geste, une attestation validée resterait
  // « disponible » indéfiniment, y compris pour un salarié qui en redemande
  // une seconde. Terminal côté écran : une fois fait, l'état passe à
  // « remise » et plus aucune action ne reste sur la ligne.
  const marquerRemise = async (demande) => {
    if (remiseEnCours) return;
    setRemiseEnCours(demande.id);
    setErreurDecision(null);
    try {
      const reponse = await apiService.marquerAttestationRemise(demande.id);
      if (reponse && reponse.ok) {
        await charger();
      } else {
        setErreurDecision(reponse?.message || t('supervisionRh.erreurGenerique'));
      }
    } catch (e) {
      setErreurDecision(messageDeRefus(e));
    } finally {
      setRemiseEnCours(null);
    }
  };

  // Un reçu se regarde avant de valider la dépense qu'il prouve. Un échec
  // d'ouverture s'affiche comme un refus de décision : la file reste en place.
  const voirJustificatif = useCallback(async (demande, piece) => {
    setErreurDecision(null);
    try {
      await ouvrirJustificatif(demande, piece);
    } catch (e) {
      signalerErreur(e, 'SupervisionRh.voirJustificatif');
      setErreurDecision(t('supervisionRh.justificatifIndisponible'));
    }
  }, [t]);

  // L'attestation ne se « regarde » pas avant décision comme un reçu : la RH
  // la télécharge pour l'imprimer et la remettre en main propre — patron de
  // `OrdresMission.js` (`downloadBlob`), jamais un nouvel onglet.
  const telechargerAttestation = useCallback(async (demande, piece) => {
    setErreurDecision(null);
    try {
      const blob = await apiService.lireJustificatif(demande.id, demande.type, piece.id);
      apiService.downloadBlob(blob, piece.nom || 'attestation.pdf');
    } catch (e) {
      signalerErreur(e, 'SupervisionRh.telechargerAttestation');
      setErreurDecision(t('supervisionRh.justificatifIndisponible'));
    }
  }, [t]);

  const demandesAffichees = useMemo(() => {
    // `setSearchQuery` de TableList rend un tableau de termes (un badge par
    // terme validé) : on ne retient que le premier, cet écran ne propose
    // qu'une recherche simple (même patron que PresenceDuJour.js).
    const brut = Array.isArray(recherche) ? recherche[0] : recherche;
    const terme = (brut || '').trim().toLowerCase();
    return demandes
      .filter((demande) => filtreEtat === 'tous' || demande.etat === filtreEtat)
      .filter((demande) => !terme || (demande.demandeur?.nom || '').toLowerCase().includes(terme));
  }, [demandes, filtreEtat, recherche]);

  const colonnes = useMemo(
    () => [
      {
        key: 'demandeur',
        label: t('supervisionRh.colDemandeur'),
        render: (demande) => (
          <span data-testid={`supervision-ligne-${demande.id}`}>{demande.demandeur?.nom || '—'}</span>
        ),
      },
      {
        key: 'type',
        label: t('supervisionRh.colType'),
        render: (demande) => demande.contenu?.libelle || demande.type,
      },
      {
        key: 'objet',
        label: t('supervisionRh.colObjet'),
        // Chaque type dit ce qui le définit : des dates pour un congé, un
        // montant et son mois de retenue pour une avance. Une colonne qui ne
        // saurait lire que les dates laisse la RH décider d'une avance sans
        // en voir le montant.
        render: (demande) => {
          const identifiant = `supervision-objet-${demande.id}`;
          const contenu = demande.contenu || {};
          // Une note de frais se juge sur pièce : la ligne dit la dépense et
          // ouvre le reçu qui la prouve (Yros/Kavero#18).
          if (demande.type === 'note_frais') {
            const parts = [
              Number.isFinite(contenu.montant) ? formatMoney(contenu.montant, localeSynthese) : null,
              contenu.nature ? t(`notesFrais.natures.${contenu.nature}`, { defaultValue: contenu.nature }) : null,
              contenu.date_depense ? formaterDate(contenu.date_depense) : null,
            ].filter(Boolean);
            return (
              <span data-testid={identifiant} className="flex flex-col gap-1">
                <span>{parts.length ? parts.join(' · ') : '—'}</span>
                {(contenu.justificatifs || []).length > 0 && (
                  <span className="flex flex-wrap gap-x-3 gap-y-1">
                    {contenu.justificatifs.map((piece) => (
                      <button
                        key={piece.id}
                        type="button"
                        data-testid={`supervision-justificatif-${demande.id}-${piece.id}`}
                        onClick={() => voirJustificatif(demande, piece)}
                        aria-label={`${t('supervisionRh.voirJustificatif')} : ${piece.nom}`}
                        title={piece.nom}
                        className="inline-flex max-w-full items-center gap-1.5 text-left text-xs font-medium text-[--color-primary] hover:underline"
                      >
                        <FaPaperclip className="flex-shrink-0" aria-hidden="true" />
                        {/* File dense : le nom se tronque, entier au survol. */}
                        <span className="max-w-[11rem] truncate">{piece.nom}</span>
                      </button>
                    ))}
                  </span>
                )}
              </span>
            );
          }
          // L'attestation se remet en main propre (Yros/Kavero#122,
          // quatrième revirement) : la RH télécharge ici le document généré
          // au dépôt pour l'imprimer — jamais le salarié. Téléchargement
          // direct (patron `OrdresMission.js`), pas un aperçu en onglet.
          if (demande.type === 'attestation') {
            const piece = (contenu.justificatifs || [])[0];
            return (
              <span data-testid={identifiant} className="flex flex-col gap-1">
                {piece ? (
                  <button
                    type="button"
                    data-testid={`supervision-justificatif-${demande.id}-${piece.id}`}
                    onClick={() => telechargerAttestation(demande, piece)}
                    aria-label={`${t('supervisionRh.telechargerAttestation')} : ${piece.nom}`}
                    title={piece.nom}
                    className="inline-flex max-w-full items-center gap-1.5 text-left text-xs font-medium text-[--color-primary] hover:underline"
                  >
                    <FaDownload className="flex-shrink-0" aria-hidden="true" />
                    <span className="max-w-[11rem] truncate">{piece.nom}</span>
                  </button>
                ) : '—'}
              </span>
            );
          }
          if (TYPES_MONETAIRES.includes(demande.type)) {
            const montant = contenu.montant;
            const parts = [
              Number.isFinite(montant) ? formatMoney(montant, localeSynthese) : null,
              periodeMonetaire(demande),
            ].filter(Boolean);
            return (
              <span data-testid={identifiant} className="flex flex-col gap-1">
                <span>{parts.length ? parts.join(' · ') : '—'}</span>
                <PiecesJointes demande={demande} />
              </span>
            );
          }
          // Une demande « Autres » se lit : l'objet que le salarié a écrit, son
          // motif, et — une fois décidée — la suite que la RH a retenue.
          if (demande.type === 'autres') {
            return (
              <span data-testid={identifiant} className="flex flex-col gap-1">
                <span className="whitespace-pre-line break-words">{contenu.objet || '—'}</span>
                {contenu.motif && (
                  <span className="text-xs text-slate-500">
                    {t(`demandePret.motifs.${contenu.motif}`, { defaultValue: contenu.motif })}
                  </span>
                )}
                {contenu.action && (
                  <span data-testid={`supervision-action-retenue-${demande.id}`} className="text-xs font-medium text-slate-700">
                    {t('supervisionRh.actionRetenue', { nom: contenu.action.nom })}
                  </span>
                )}
                <PiecesJointes demande={demande} />
              </span>
            );
          }
          const debut = contenu.date_debut;
          const fin = contenu.date_fin;
          return (
            <span data-testid={identifiant} className="flex flex-col gap-1">
              <span>{debut && fin ? `${debut} → ${fin}` : '—'}</span>
              <PiecesJointes demande={demande} />
            </span>
          );
        },
      },
      {
        key: 'etat',
        label: t('supervisionRh.colEtat'),
        render: (demande) => (
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex items-center w-fit text-xs font-medium px-2.5 py-1 rounded-full ${
                COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
              }`}
            >
              {t(`supervisionRh.etats.${demande.etat}`, { defaultValue: demande.etat })}
            </span>
            {/* Motif de refus : jamais un texte de repli — le champ n'existe
                côté C10 que pour ce cas précis (plan § Symboles). */}
            {demande.etat === 'refusee' && demande.motif_refus && (
              <span data-testid={`supervision-motif-${demande.id}`} className="text-xs text-gray-500">
                {demande.motif_refus}
              </span>
            )}
            <EtapeManager demande={demande} />
          </div>
        ),
      },
      {
        key: 'validateur',
        label: t('supervisionRh.colValidateur'),
        render: (demande) => demande.validateur?.nom || '—',
      },
      {
        key: 'solde',
        label: t('supervisionRh.colSolde'),
        // Ce que « solde » veut dire dépend du type : un reliquat de jours
        // pour un congé, la borne du montant pour une avance. Le proxy
        // n'accroche plus le solde de congés aux autres types (bolt 090),
        // mais l'écran ne s'y fie pas et lit le type lui-même.
        render: (demande) => {
          if (TYPES_MONETAIRES.includes(demande.type)) {
            const plafond = demande.contenu?.plafond;
            // Plafond absent : tiret. Surtout pas zéro, qui se lirait comme
            // une borne à zéro, donc comme un refus de toute demande.
            return Number.isFinite(plafond) && plafond > 0 ? formatMoney(plafond, localeSynthese) : '—';
          }
          return afficherSolde(demande.solde);
        },
      },
      {
        key: 'date_demande',
        label: t('supervisionRh.colDemandeLe'),
        // Ajoutée en FIN de tableau : le contrat livré vérifie les six
        // premières colonnes par index, et une colonne insérée au milieu les
        // décalerait toutes.
        render: (demande) => formaterDate(demande.date_demande),
      },
    ],
    // `localeSynthese` : la durée des heures déclarées suit la langue de
    // l'interface, elle doit se reformater quand celle-ci change.
    [t, voirJustificatif, localeSynthese]
  );

  // Les actions n'existent que sur une demande en attente : une demande
  // décidée ne se re-décide pas, et Odoo le refuserait de toute façon.
  //
  // Exception pour l'attestation (Yros/Kavero#122, quatrième revirement du
  // 2026-09-25) : une fois validée, la RH garde un unique geste — marquer sa
  // remise en main propre une fois le document (déjà téléchargeable depuis
  // la colonne « objet ») imprimé et donné au salarié.
  const actions = (demande) => {
    if (demande.type === 'attestation' && demande.etat === 'validee') {
      return (
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            data-testid={`supervision-remise-attestation-${demande.id}`}
            disabled={remiseEnCours !== null}
            onClick={() => marquerRemise(demande)}
            className="px-3 py-1.5 text-xs font-medium rounded-full bg-[--color-primary] text-white hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {remiseEnCours === demande.id ? t('supervisionRh.enregistrementRemise') : t('supervisionRh.marquerRemis')}
          </button>
        </div>
      );
    }
    if (demande.etat !== 'en_attente') {
      return null;
    }
    return (
      <div className="flex items-center gap-2 justify-end">
        {demande.type === 'autres' && actionsAutres.length > 0 && (
          <select
            data-testid={`supervision-action-${demande.id}`}
            aria-label={t('supervisionRh.actionLabel')}
            value={actionChoisie[demande.id] || ''}
            disabled={decisionEnCours !== null}
            onChange={(e) => setActionChoisie((prev) => ({ ...prev, [demande.id]: e.target.value }))}
            className="max-w-[11rem] px-2 py-1 text-xs border rounded-lg text-gray-700 bg-gray-50"
          >
            <option value="">{t('supervisionRh.actionAucune')}</option>
            {actionsAutres.map((action) => (
              <option key={action.id} value={action.id}>{action.nom}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          data-testid={`supervision-approuver-${demande.id}`}
          disabled={decisionEnCours !== null}
          onClick={() => approuver(demande)}
          className="px-3 py-1.5 text-xs font-medium rounded-full bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('supervisionRh.approuver')}
        </button>
        <button
          type="button"
          data-testid={`supervision-refuser-${demande.id}`}
          disabled={decisionEnCours !== null}
          onClick={() => {
            setRefusPour(demande);
            setMotif('');
            setErreurDecision(null);
          }}
          className="px-3 py-1.5 text-xs font-medium rounded-full border border-red-600 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('supervisionRh.refuser')}
        </button>
      </div>
    );
  };

  // Barre de filtres locale, au-dessus du tableau (`toolbarActions` de
  // TableList) : aucun des 4 boutons ne redéclenche `listerSupervisionRh`.
  const barreFiltres = (
    <>
      {FILTRES.map((cle) => (
        <button
          key={cle}
          type="button"
          data-testid={`supervision-filtre-${cle}`}
          aria-pressed={filtreEtat === cle}
          onClick={() => setFiltreEtat(cle)}
          className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors duration-200 ${
            filtreEtat === cle
              ? 'bg-[--color-primary] text-white border-[--color-primary]'
              : 'bg-white text-[--color-primary] border-[--color-primary] hover:bg-[--color-primary] hover:text-white'
          }`}
        >
          {t(`supervisionRh.filtres.${cle}`)}
        </button>
      ))}
    </>
  );

  return (
    <div className="p-4">
      <HeaderCard title={t('supervisionRh.titre')} text={t('supervisionRh.description')} />

      {erreur && (
        <p
          data-testid="supervision-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
        >
          {erreur}
        </p>
      )}

      {/* Erreur de décision : même identifiant de test, mais jamais affichée
          en même temps que celle du chargement — si la liste n'a pas chargé,
          aucune action n'est offerte. */}
      {!erreur && erreurDecision && (
        <p
          data-testid="supervision-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3"
        >
          {erreurDecision}
        </p>
      )}

      {!erreur && refusPour && (
        <div
          data-testid="supervision-refus-panneau"
          className="bg-white border border-gray-200 rounded-xl p-4 mb-3 space-y-3 shadow-sm"
        >
          <h3 className="text-sm font-semibold">{t('supervisionRh.motifTitre')}</h3>
          <textarea
            data-testid="supervision-motif-saisie"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder={t('supervisionRh.motifPlaceholder')}
            rows={3}
            className="w-full px-3 py-2 border rounded-lg text-sm text-gray-700 bg-gray-50"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              data-testid="supervision-refus-annuler"
              onClick={() => {
                setRefusPour(null);
                setMotif('');
              }}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              {t('supervisionRh.annuler')}
            </button>
            <button
              type="button"
              data-testid="supervision-refus-confirmer"
              disabled={decisionEnCours !== null || !motif.trim()}
              onClick={confirmerLeRefus}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('supervisionRh.confirmer')}
            </button>
          </div>
        </div>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : (
        !erreur && (
          <div className="space-y-4">
            <SyntheseAvances
              demandes={demandes}
              locale={localeSynthese}
              libelles={LIBELLES_SYNTHESE_RH}
              prefixe="supervision"
            />
            <TableList
              columns={colonnes}
              data={demandesAffichees}
              searchQuery={recherche}
              setSearchQuery={setRecherche}
              showFilter={false}
              toolbarActions={barreFiltres}
              extraActions={actions}
              stickyActions
            />
            <TableauRemboursements
              demandes={demandes}
              locale={localeSynthese}
              prefixe="supervision"
              titre="supervisionRh.echeancierTitre"
              vide="supervisionRh.echeancierVide"
              afficherDemandeur
              outils
            />
          </div>
        )
      )}
    </div>
  );
}

export default SupervisionRh;
